import { DEFAULT_SESSION_CONFIG } from '@Medsicheck/medsi-web-sdk';
import { UAParser } from 'ua-parser-js';

import type { RppgUploadMetadata } from 'services/rppg/api';

export const RPPG_CLIENT_NAME = 'medsi_check';

export interface GeoPosition {
  latitude: number;
  longitude: number;
  altitude?: number | null;
  accuracy?: number | null;
}

const GEO_TIMEOUT_MS = 15_000;

function buildLocationMetadata(
  geoPosition: GeoPosition | null | undefined,
): RppgUploadMetadata['location'] | undefined {
  if (!geoPosition) {
    return undefined;
  }

  const { latitude, longitude, altitude, accuracy } = geoPosition;
  if (latitude == null || longitude == null) {
    return undefined;
  }

  return {
    granularity: 'precise',
    source: 'gps',
    lat: latitude,
    lng: longitude,
    alt: altitude ?? null,
    accuracy_m: accuracy ?? null,
  };
}

function buildDeviceMetadata(): RppgUploadMetadata['device'] {
  const parser = new UAParser();
  const result = parser.getResult();
  const osName = result.os.name ?? 'unknown';
  const osVersion = result.os.version ?? 'unknown';
  const browserName = result.browser.name ?? undefined;
  const browserVersion = result.browser.version ?? undefined;
  const model =
    result.device.model ?? result.device.vendor ?? undefined;

  return {
    platform: 'web',
    os: osName,
    os_version: osVersion,
    app_version: process.env.npm_package_version,
    browser: browserName,
    browser_version: browserVersion,
    model,
    camera: {
      facing: 'front',
      requested_fps: DEFAULT_SESSION_CONFIG.fps,
    },
  };
}

export function buildRppgUploadMetadata(
  geoPosition?: GeoPosition | null,
): RppgUploadMetadata {
  const metadata: RppgUploadMetadata = {
    schema_version: 1,
    client_name: RPPG_CLIENT_NAME,
    device: buildDeviceMetadata(),
  };

  const location = buildLocationMetadata(geoPosition);
  if (location) {
    metadata.location = location;
  }

  return metadata;
}

export function requestGeoPosition(): Promise<GeoPosition | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          altitude: position.coords.altitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => resolve(null),
      {
        enableHighAccuracy: true,
        timeout: GEO_TIMEOUT_MS,
        maximumAge: 60_000,
      },
    );
  });
}
